import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys, type OrgTreeNode } from '@vtak/shared';
import { workspaceClient } from '@/services/workspace-client';

/** An organization together with its full unit tree */
export interface OrgWithTree {
  orgId: string;
  orgName: string;
  tree: OrgTreeNode[];
}

/**
 * Load every organization's unit tree — drives the admin "preview as unit"
 * switcher. Enabled lazily so non-admins never fetch it.
 */
export function useOrgTreesQuery(enabled: boolean): UseQueryResult<OrgWithTree[]> {
  return useQuery({
    queryKey: queryKeys.workspace.orgTrees,
    enabled,
    staleTime: 60_000,
    queryFn: fetchOrgTrees,
  });
}

/** Fetch all orgs, then each org's tree */
async function fetchOrgTrees(): Promise<OrgWithTree[]> {
  const orgs = await workspaceClient.getOrgs();
  return Promise.all(
    orgs.map(async (org) => ({
      orgId: org.id,
      orgName: org.name,
      tree: await workspaceClient.getOrgTree(org.id),
    })),
  );
}
